import toast from 'react-hot-toast';

/**
 * Sistema de notificaciones toast para transacciones blockchain.
 * Envuelve react-hot-toast con estilos y mensajes específicos.
 */

export const txToast = {
  /**
   * Muestra un toast mientras una transacción está en proceso.
   * Devuelve el ID del toast para actualizarlo después.
   */
  loading(message = 'Procesando transacción...') {
    return toast.loading(message, {
      style: {
        background: '#1e1b4b',
        color: '#e0e7ff',
        border: '1px solid #4338ca',
      },
      iconTheme: { primary: '#818cf8', secondary: '#1e1b4b' },
    });
  },

  /**
   * Actualiza un toast existente a éxito.
   */
  success(toastId, message, txHash) {
    toast.success(
      <div>
        <p className="font-semibold">{message}</p>
        {txHash && (
          <p className="text-xs mt-1 opacity-80 break-all">TX: {txHash}</p>
        )}
      </div>,
      {
        id: toastId,
        duration: 5000,
        style: {
          background: '#052e16',
          color: '#bbf7d0',
          border: '1px solid #16a34a',
        },
        iconTheme: { primary: '#22c55e', secondary: '#052e16' },
      }
    );
  },

  /**
   * Actualiza un toast existente a error.
   */
  error(toastId, message) {
    const shortMsg =
      message.length > 120 ? message.substring(0, 120) + '...' : message;
    toast.error(shortMsg, {
      id: toastId,
      duration: 6000,
      style: {
        background: '#450a0a',
        color: '#fecaca',
        border: '1px solid #dc2626',
      },
      iconTheme: { primary: '#ef4444', secondary: '#450a0a' },
    });
  },

  /**
   * Toast informativo simple.
   */
  info(message) {
    toast(message, {
      icon: 'ℹ️',
      duration: 4000,
      style: {
        background: '#1e3a5f',
        color: '#bfdbfe',
        border: '1px solid #3b82f6',
      },
    });
  },
};

/**
 * Helper para ejecutar una transacción con toasts automáticos.
 * @param {Function} txFn - Función async que ejecuta la transacción
 * @param {string} loadingMsg - Mensaje durante la carga
 * @param {string} successMsg - Mensaje al completar
 * @returns {object} Resultado de la transacción
 */
export async function withTxToast(txFn, loadingMsg, successMsg) {
  const toastId = txToast.loading(loadingMsg);
  try {
    const result = await txFn();
    // result.status puede ser false (0) o 0n (BigInt) si la transacción fue revertida
    const status = result?.status;
    if (status === false || status === 0n || status === 0) {
      const msg = 'La transacción fue revertida por el contrato';
      txToast.error(toastId, msg);
      throw new Error(msg);
    }
    const txHash = result?.transactionHash || result?.hash || '';
    txToast.success(toastId, successMsg, txHash);
    return result;
  } catch (err) {
    const message = err?.message || 'Error desconocido';
    txToast.error(toastId, message);
    throw err;
  }
}
